import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  ImageIcon,
  Loader2,
  MapPin,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { MapperMap } from "@/components/mapping/MapperMap";
import { PILFormSheet, type PILData } from "@/components/mapping/PILFormSheet";
import { PILStatusChip } from "@/components/mapping/PILStatusChip";
import { DirectionsLink } from "@/components/mapping/DirectionsLink";
import { PhotoCapture, type UploadedPhoto } from "@/components/mapping/PhotoCapture";
import { FieldInfoButton } from "@/components/mapping/FieldInfoButton";
import { HelpFAB } from "@/components/mapping/HelpFAB";
import { Stepper } from "@/components/mapping/Stepper";
import { LOCATION_FIELD_HELP } from "@/components/mapping/mappingHelpContent";
import { useUserPosition } from "@/hooks/useUserPosition";
import { useForceLightTheme } from "@/hooks/useForceLightTheme";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { getEffectiveRadius } from "@/lib/sport-defaults";
import { cn } from "@/lib/utils";

interface Court {
  id: string;
  lat: number;
  lng: number;
  sport_type: string;
  search_radius_m: number | null;
}

interface LocationPhoto {
  id: string;
  kind: "overview" | "degradation";
  url: string;
}

interface LocationData {
  id: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  mapping_status: string;
  mapping_public_access: boolean | null;
  mapping_open_24_7: boolean | null;
  mapping_opening_hours: string | null;
  mapping_passing_by_visibility: number | null;
  mapping_nearby_habitations: number | null;
  mapping_signs_of_degradation: boolean | null;
  mapping_janitors: boolean | null;
  mapping_lighting_present: boolean | null;
  mapping_dubious_people: boolean | null;
  mapping_video_cameras: boolean | null;
  min_installation_spots: number;
  // Stream 1 additions surfaced via getMappingLocation:
  step1Done: boolean;
  min_spots_override: boolean | null;
  location_step1_done_at: string | null;
  location_photos: LocationPhoto[];
  pils: PILData[];
  courts: Court[];
}

export function MappingLocationPage() {
  useForceLightTheme();
  const params = useParams({ strict: false }) as { token?: string; locationId?: string };
  const token = params.token as string;
  const locationId = params.locationId as string;
  const navigate = useNavigate();

  const [expandedPilId, setExpandedPilId] = useState<string | null>(null);
  const pilRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const overviewPhotosRef = useRef<HTMLDivElement | null>(null);

  const scrollToOverviewPhotos = useCallback(() => {
    const el = overviewPhotosRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
    setTimeout(() => {
      el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2");
    }, 2500);
  }, []);
  const userPos = useUserPosition();

  const utils = trpc.useUtils?.();

  const query = trpc.share.getMappingLocation.useQuery(
    { token, locationId },
    { enabled: !!token && !!locationId },
  );

  const location: LocationData | undefined = useMemo(() => {
    const data = query.data as
      | { location: any; pils: PILData[]; sport_courts: Court[]; location_photos?: LocationPhoto[] }
      | undefined;
    if (!data?.location) return undefined;
    return {
      ...data.location,
      location_photos: data.location.location_photos ?? data.location_photos ?? [],
      pils: data.pils ?? [],
      courts: data.sport_courts ?? [],
    };
  }, [query.data]);

  const createPIL = trpc.share.createMapperPIL.useMutation({
    onSuccess: (created: any) => {
      query.refetch();
      if (created?.id) {
        setExpandedPilId(created.id);
        requestAnimationFrame(() => scrollToPil(created.id));
      }
      toast.success("Installation spot added");
    },
    onError: (err) => toast.error(err.message),
  });

  const setLocFields = trpc.share.setLocationMappingFields.useMutation({
    onError: (err) => toast.error(`Failed to save: ${err.message}`),
    onSuccess: () => utils?.share?.getMappingLocation?.invalidate?.({ token, locationId }),
  });

  // Stream 1 mutations: step-1 gate + min-spots override.
  const markStep1Done = trpc.share.markStep1Done.useMutation({
    onSuccess: () => {
      utils?.share?.getMappingLocation?.invalidate?.({ token, locationId });
      utils?.share?.listMappingLocations?.invalidate?.();
      toast.success("Step 1 done — installation spots unlocked");
    },
    onError: (err) => toast.error(err.message),
  });

  const unmarkStep1Done = trpc.share.unmarkStep1Done.useMutation({
    onSuccess: () => {
      utils?.share?.getMappingLocation?.invalidate?.({ token, locationId });
      utils?.share?.listMappingLocations?.invalidate?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const setMinSpotsOverride = trpc.share.setMinSpotsOverride.useMutation({
    onSuccess: () => {
      utils?.share?.getMappingLocation?.invalidate?.({ token, locationId });
      utils?.share?.listMappingLocations?.invalidate?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const scrollToPil = useCallback((pilId: string) => {
    const el = pilRefs.current.get(pilId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-primary");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 1500);
    }
  }, []);

  const handlePilPinClick = useCallback(
    (pilId: string) => {
      setExpandedPilId(pilId);
      requestAnimationFrame(() => scrollToPil(pilId));
    },
    [scrollToPil],
  );

  const [fetchingGps, setFetchingGps] = useState(false);

  const handleAddAtCenter = useCallback(() => {
    if (!location) return;

    // Fast path: use the live position from useUserPosition (watchPosition keeps
    // it ≤ 5s fresh). Avoids a 2-5s getCurrentPosition round-trip.
    if (userPos) {
      createPIL.mutate({ token, locationId, lat: userPos.lat, lng: userPos.lng });
      return;
    }

    if (!navigator.geolocation) {
      createPIL.mutate({ token, locationId, lat: location.lat, lng: location.lng });
      return;
    }

    setFetchingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFetchingGps(false);
        createPIL.mutate({
          token,
          locationId,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        setFetchingGps(false);
        createPIL.mutate({ token, locationId, lat: location.lat, lng: location.lng });
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }, [location, createPIL, token, locationId, userPos]);

  const pilPins = useMemo(() => {
    return (location?.pils ?? []).map((p) => ({
      id: p.id,
      lat: p.lat,
      lng: p.lng,
      status: p.status,
      ordinal: p.ordinal,
    }));
  }, [location?.pils]);

  const courtPins = useMemo(() => {
    return (location?.courts ?? []).map((c) => ({
      id: c.id,
      lat: c.lat,
      lng: c.lng,
      sportType: c.sport_type,
      radius: getEffectiveRadius(c.sport_type, c.search_radius_m),
    }));
  }, [location?.courts]);

  const uploadLocPhoto = trpc.share.uploadLocationPhoto.useMutation({
    onSuccess: () => utils?.share?.getMappingLocation?.invalidate?.({ token, locationId }),
    onError: (err) => toast.error(`Photo upload failed: ${err.message}`),
  });

  const deleteLocPhoto = trpc.share.deleteLocationPhoto.useMutation({
    onSuccess: () => utils?.share?.getMappingLocation?.invalidate?.({ token, locationId }),
    onError: (err) => toast.error(`Failed to delete photo: ${err.message}`),
  });

  // Local draft for location-level fields, autosave on blur/change
  const [publicAccess, setPublicAccess] = useState<boolean | null>(null);
  const [open247, setOpen247] = useState<boolean | null>(null);
  const [openingHours, setOpeningHours] = useState<string>("");
  const [openingHoursDirty, setOpeningHoursDirty] = useState(false);
  const [passingByVisibility, setPassingByVisibility] = useState<number | null>(null);
  const [nearbyHabitations, setNearbyHabitations] = useState<number | null>(null);
  const [signsOfDegradation, setSignsOfDegradation] = useState<boolean | null>(null);
  const [janitors, setJanitors] = useState<boolean | null>(null);
  const [lighting, setLighting] = useState<boolean | null>(null);
  const [dubiousPeople, setDubiousPeople] = useState<boolean | null>(null);
  const [videoCameras, setVideoCameras] = useState<boolean | null>(null);

  useEffect(() => {
    if (!location) return;
    setPublicAccess(location.mapping_public_access);
    setOpen247(location.mapping_open_24_7);
    setOpeningHours(location.mapping_opening_hours ?? "");
    setOpeningHoursDirty(false);
    setPassingByVisibility(location.mapping_passing_by_visibility);
    setNearbyHabitations(location.mapping_nearby_habitations);
    setSignsOfDegradation(location.mapping_signs_of_degradation);
    setJanitors(location.mapping_janitors);
    setLighting(location.mapping_lighting_present);
    setDubiousPeople(location.mapping_dubious_people);
    setVideoCameras(location.mapping_video_cameras);
  }, [location]);

  const saveLocField = useCallback(
    (patch: Record<string, unknown>) => {
      setLocFields.mutate({ token, locationId, patch });
    },
    [setLocFields, token, locationId],
  );

  const normalizedLocationIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!location) return;
    if (normalizedLocationIdRef.current === location.id) return;
    normalizedLocationIdRef.current = location.id;

    const patch: Record<string, unknown> = {};
    if (location.mapping_public_access === null) patch.publicAccess = false;
    if (location.mapping_open_24_7 === null) patch.open247 = false;
    if (location.mapping_signs_of_degradation === null) patch.signsOfDegradation = false;
    if (location.mapping_janitors === null) patch.janitors = false;
    if (location.mapping_lighting_present === null) patch.lighting = false;
    if (location.mapping_dubious_people === null) patch.dubiousPeople = false;
    if (location.mapping_video_cameras === null) patch.videoCameras = false;
    if (location.mapping_passing_by_visibility === null) patch.passingByVisibility = 3;
    if (location.mapping_nearby_habitations === null) patch.nearbyHabitations = 3;

    if (Object.keys(patch).length > 0) saveLocField(patch);
  }, [location, saveLocField]);

  const overviewPhotos = useMemo(
    () => (location?.location_photos ?? []).filter((p) => p.kind === "overview") as UploadedPhoto[],
    [location?.location_photos],
  );

  const pilsLocked = overviewPhotos.length < 3;

  const degradationPhotos = useMemo(
    () => (location?.location_photos ?? []).filter((p) => p.kind === "degradation") as UploadedPhoto[],
    [location?.location_photos],
  );

  const handleLocPhotoUpload = useCallback(
    async (kind: "overview" | "degradation", base64: string) => {
      await uploadLocPhoto.mutateAsync({ token, locationId, kind, base64 });
    },
    [token, locationId, uploadLocPhoto],
  );

  const handleLocPhotoDelete = useCallback(
    async (photoId: string) => {
      await deleteLocPhoto.mutateAsync({ token, locationId, photoId });
    },
    [token, locationId, deleteLocPhoto],
  );

  if (query.isLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (query.error || !location) {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 px-6 text-center bg-background">
        <MapPin className="h-10 w-10 text-muted-foreground" />
        <p className="font-medium">Location unavailable</p>
        <Button variant="outline" onClick={() => navigate({ to: "/mapping/$token", params: { token } } as any)}>
          Back to list
        </Button>
      </div>
    );
  }

  const activePils = location.pils.filter((p) => p.status !== "rejected");
  const totalPins = activePils.length;
  const donePins = activePils.filter((p) => p.status === "done").length;
  const minRequired = location.min_installation_spots ?? 1;
  const denominator = Math.max(minRequired, totalPins);
  const pinsNeeded = Math.max(0, minRequired - totalPins);

  // Stream 2 / 3 wiring (section A):
  // Step 1 must be marked done before Step 2 is unlocked. The overview
  // photo gate (pilsLocked) still applies inside Step 1 itself.
  const step1Done = location.step1Done === true;
  const minSpotsOverride = location.min_spots_override === true;
  const allPilsDone = totalPins > 0 && donePins === totalPins;
  // "Mark location done" appears when:
  //   - Step 1 is done, AND
  //   - either we hit min spots OR the override flag is on, AND
  //   - every active PIL is `done`.
  const canMarkLocationDone =
    step1Done && allPilsDone && (donePins >= minRequired || minSpotsOverride);

  return (
    <div className="flex h-[100dvh] flex-col bg-background">
      <header className="flex shrink-0 items-center gap-2 border-b px-2 py-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11"
          onClick={() => navigate({ to: "/mapping/$token", params: { token } } as any)}
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">{location.name}</h1>
          {location.address && (
            <p className="truncate text-xs text-muted-foreground">{location.address}</p>
          )}
        </div>
        <PILStatusChip status={location.mapping_status} />
      </header>

      {/* Stepper — section A. Lives between the header and the scrollable
          body so it stays visible while the user scrolls within a step. */}
      <div className="border-b bg-background px-4 py-3">
        <Stepper
          steps={[
            { id: "step-1", label: "Location" },
            { id: "step-2", label: "Installation spots" },
          ]}
          currentStep={step1Done ? 2 : 1}
          completedThrough={step1Done ? 1 : 0}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {!step1Done && pilsLocked && (
          <div className="mx-4 mt-4 mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-3">
            <p className="text-sm font-semibold text-amber-900">
              Upload 3 overview photos of this location before editing spots.
            </p>
            <p className="mt-0.5 text-xs text-amber-900/80">
              {overviewPhotos.length}/3 done.
            </p>
            <div className="mt-2 flex justify-end">
              <Button
                type="button"
                onClick={scrollToOverviewPhotos}
                className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                size="sm"
              >
                <ImageIcon className="h-4 w-4" />
                Take pictures
              </Button>
            </div>
          </div>
        )}

        {/* Step 1 done banner — collapses Step 1 content into a compact
            confirmation when the gate has been crossed. The "Edit Step 1"
            link calls `unmarkStep1Done`, returning the page to Step 1. */}
        {step1Done && (
          <div className="mx-4 mt-4 mb-2 flex items-center justify-between gap-3 rounded-md border border-green-300 bg-green-50 px-3 py-2">
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-900">
              <Check className="h-4 w-4" strokeWidth={3} />
              Step 1 — Done
            </p>
            <button
              type="button"
              onClick={() => unmarkStep1Done.mutate({ token, locationId })}
              disabled={unmarkStep1Done.isPending}
              className="text-xs font-medium text-green-900 underline underline-offset-2 hover:text-green-950 disabled:opacity-50"
            >
              {unmarkStep1Done.isPending ? "Reopening…" : "Edit Step 1"}
            </button>
          </div>
        )}

        {/* Map — always visible across both steps so the mapper has spatial
            context. Sport-court overlays render here and must paint above
            PIL pins (handled inside MapperMap, section C). */}
        <div className="relative h-64 shrink-0">
          <MapperMap
            className="h-full w-full"
            defaultCenter={{ lat: location.lat, lng: location.lng }}
            defaultZoom={18}
            pils={pilPins}
            courts={courtPins}
            selectedPilId={expandedPilId}
            userPos={userPos}
            onPilClick={handlePilPinClick}
          />
          <div className="absolute left-2 top-2 flex gap-2">
            <DirectionsLink lat={location.lat} lng={location.lng} name={location.name} />
          </div>
        </div>

        {/* ===== Step 1: Location ===== */}
        {!step1Done && (
        <section className="space-y-2 border-b px-4 py-4">
          <h2 className="text-sm font-semibold">About this location</h2>

          {/* Overview photos */}
          <div
            ref={overviewPhotosRef}
            className="space-y-2 rounded-md p-2 scroll-mt-4 transition-[box-shadow] duration-300"
          >
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-semibold">
                Overview photos <span className="text-muted-foreground font-normal">(≥3 required)</span>
              </Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.overviewPhotos} />
            </div>
            <PhotoCapture
              photos={overviewPhotos}
              onUpload={(b64) => handleLocPhotoUpload("overview", b64)}
              onDelete={handleLocPhotoDelete}
              minRequired={3}
            />
          </div>

          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm">Public access</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.publicAccess} />
            </div>
            <Switch
              checked={publicAccess === true}
              onCheckedChange={(v) => {
                setPublicAccess(v);
                saveLocField({ publicAccess: v });
              }}
              className="scale-110"
            />
          </div>

          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm">Open 24/7</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.open247} />
            </div>
            <Switch
              checked={open247 === true}
              onCheckedChange={(v) => {
                setOpen247(v);
                saveLocField({ open247: v });
              }}
              className="scale-110"
            />
          </div>

          {open247 !== true && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label className="text-sm">Opening hours</Label>
                <FieldInfoButton {...LOCATION_FIELD_HELP.openingHours} />
              </div>
              <Input
                value={openingHours}
                onChange={(e) => {
                  setOpeningHours(e.target.value);
                  setOpeningHoursDirty(true);
                }}
                onBlur={() => {
                  if (!openingHoursDirty) return;
                  saveLocField({ openingHours: openingHours || null });
                  setOpeningHoursDirty(false);
                }}
                placeholder="e.g. Mon–Fri 8am–10pm"
                className="min-h-11"
              />
            </div>
          )}

          {/* Passing-by visibility */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-semibold">Passing-by visibility</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.passingByVisibility} />
            </div>
            <p className="text-xs text-muted-foreground">1 = hidden, 5 = highly visible</p>
            <div className="px-1 pt-1">
              {/* Section B — slider edge-swipe fix.
                  Approach taken: pan-y on the thumb (NOT the root). The
                  Radix slider relies on horizontal pointer events on the
                  thumb for dragging, so the touch-action override has to
                  live on the thumb itself. We forward a className through
                  the slot props using `[&_[data-slot=slider-thumb]]:touch-pan-y`
                  Tailwind selector. This was preferred to swapping the lib
                  because all the rest of the UX (value labels, focus ring,
                  keyboard nav) keeps working unchanged. */}
              <Slider
                className="[&_[data-slot=slider-thumb]]:[touch-action:pan-y]"
                min={1}
                max={5}
                step={1}
                value={[passingByVisibility ?? 3]}
                onValueChange={(v) => setPassingByVisibility(v[0] ?? null)}
                onValueCommit={(v) => saveLocField({ passingByVisibility: v[0] ?? null })}
              />
              <div className="flex justify-between text-[11px] text-muted-foreground pt-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className={cn(passingByVisibility === n && "font-bold text-foreground")}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Nearby habitations */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-semibold">Nearby habitations</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.nearbyHabitations} />
            </div>
            <p className="text-xs text-muted-foreground">1 = none nearby, 5 = dense</p>
            <div className="px-1 pt-1">
              {/* Section B — same slider edge-swipe fix as above. */}
              <Slider
                className="[&_[data-slot=slider-thumb]]:[touch-action:pan-y]"
                min={1}
                max={5}
                step={1}
                value={[nearbyHabitations ?? 3]}
                onValueChange={(v) => setNearbyHabitations(v[0] ?? null)}
                onValueCommit={(v) => saveLocField({ nearbyHabitations: v[0] ?? null })}
              />
              <div className="flex justify-between text-[11px] text-muted-foreground pt-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className={cn(nearbyHabitations === n && "font-bold text-foreground")}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Signs of degradation */}
          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm font-semibold">Signs of degradation</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.signsOfDegradation} />
            </div>
            <Switch
              checked={signsOfDegradation === true}
              onCheckedChange={(v) => {
                setSignsOfDegradation(v);
                saveLocField({ signsOfDegradation: v });
              }}
              className="scale-110"
            />
          </div>

          {signsOfDegradation && (
            <div className="space-y-2 pl-3 border-l-2 border-amber-400">
              <Label className="text-sm font-semibold">Degradation photos</Label>
              <PhotoCapture
                photos={degradationPhotos}
                onUpload={(b64) => handleLocPhotoUpload("degradation", b64)}
                onDelete={handleLocPhotoDelete}
              />
            </div>
          )}

          {/* Janitors */}
          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm">Janitors</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.janitors} />
            </div>
            <Switch
              checked={janitors === true}
              onCheckedChange={(v) => {
                setJanitors(v);
                saveLocField({ janitors: v });
              }}
              className="scale-110"
            />
          </div>

          {/* Lamppost / artificial lighting */}
          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm">Lamppost / artificial lighting</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.lighting} />
            </div>
            <Switch
              checked={lighting === true}
              onCheckedChange={(v) => {
                setLighting(v);
                saveLocField({ lighting: v });
              }}
              className="scale-110"
            />
          </div>

          {/* Dubious people nearby */}
          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm">Dubious people nearby</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.dubiousPeople} />
            </div>
            <Switch
              checked={dubiousPeople === true}
              onCheckedChange={(v) => {
                setDubiousPeople(v);
                saveLocField({ dubiousPeople: v });
              }}
              className="scale-110"
            />
          </div>

          {/* Video cameras */}
          <div className="flex items-center justify-between gap-3 min-h-11">
            <div className="flex flex-1 items-center gap-1.5">
              <Label className="text-sm">Video cameras</Label>
              <FieldInfoButton {...LOCATION_FIELD_HELP.videoCameras} />
            </div>
            <Switch
              checked={videoCameras === true}
              onCheckedChange={(v) => {
                setVideoCameras(v);
                saveLocField({ videoCameras: v });
              }}
              className="scale-110"
            />
          </div>

          {/* Step 1 completion CTA — placed at the bottom of the Step 1
              body so the mapper naturally hits it after filling everything
              in. Disabled while overview photos are below the gate. */}
          <div className="pt-3">
            <Button
              type="button"
              onClick={() => markStep1Done.mutate({ token, locationId })}
              disabled={markStep1Done.isPending || pilsLocked}
              className="w-full min-h-12 gap-2 bg-green-600 hover:bg-green-700 text-white"
              size="lg"
            >
              {markStep1Done.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {markStep1Done.isPending ? "Saving…" : "Mark Step 1 done"}
            </Button>
            {pilsLocked && (
              <p className="mt-1.5 text-xs text-muted-foreground text-center">
                Add {3 - overviewPhotos.length} more overview photo{3 - overviewPhotos.length === 1 ? "" : "s"} to unlock this.
              </p>
            )}
          </div>
        </section>
        )}

        {/* ===== Step 2: Installation spots ===== */}
        {step1Done && (
        <section className="px-4 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Installation spots</h2>
            <span className="text-xs tabular-nums text-muted-foreground">
              {donePins} of {denominator} done
            </span>
          </div>
          {/* Min-spots banner — section A. Three states:
              1. Pins below minimum AND override OFF → amber "find more" with
                 an "Only N viable on the ground" link that flips override on.
              2. Override ON → green confirmation with an "Undo" link.
              3. Pins meet/exceed minimum → no banner (silent success). */}
          {pinsNeeded > 0 && !minSpotsOverride && (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p className="font-semibold">
                Find {pinsNeeded} more installation spot{pinsNeeded === 1 ? "" : "s"}
              </p>
              <p className="text-xs text-amber-900/80">
                The admin set a minimum of {minRequired} for this location.
              </p>
              <button
                type="button"
                onClick={() =>
                  setMinSpotsOverride.mutate({ token, locationId, value: true })
                }
                disabled={setMinSpotsOverride.isPending}
                className="mt-1 inline-flex items-center gap-0.5 text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950 disabled:opacity-50"
              >
                Cannot find more viable spots? Please click here
              </button>
            </div>
          )}
          {minSpotsOverride && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-900">
              <p className="font-semibold">Min-spots override on</p>
              <button
                type="button"
                onClick={() =>
                  setMinSpotsOverride.mutate({ token, locationId, value: false })
                }
                disabled={setMinSpotsOverride.isPending}
                className="text-xs font-medium underline underline-offset-2 hover:text-green-950 disabled:opacity-50"
              >
                Undo
              </button>
            </div>
          )}

          {location.pils.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-md border border-dashed p-6 text-center">
              <MapPin className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">Find at least 1 installation spot</p>
              <p className="text-xs text-muted-foreground">
                No spots were pre-placed for this location. Walk the area and tap the map to add a potential installation spot.
              </p>
              <Button
                type="button"
                onClick={handleAddAtCenter}
                disabled={createPIL.isPending || fetchingGps || pilsLocked}
                className="mt-1 gap-2"
              >
                {createPIL.isPending || fetchingGps ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
                {fetchingGps ? "Getting GPS…" : createPIL.isPending ? "Adding…" : "Add a spot"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {location.pils.map((pil) => (
                <PILCard
                  key={pil.id}
                  pil={pil}
                  token={token}
                  expanded={expandedPilId === pil.id}
                  onToggle={() =>
                    setExpandedPilId((cur) => (cur === pil.id ? null : pil.id))
                  }
                  onChanged={() => query.refetch()}
                  registerRef={(el) => pilRefs.current.set(pil.id, el)}
                  locked={pilsLocked}
                  userPos={userPos}
                />
              ))}
            </div>
          )}
        </section>
        )}

        {/* Bottom action — section A:
            - In Step 1, hide entirely; the "Mark Step 1 done" CTA inside
              the section drives progression.
            - In Step 2, by default show "Add installation spot at my
              location". Once `canMarkLocationDone` flips on, replace it
              with a green "Mark location done" button. We don't call a
              dedicated mutation: the server's recomputeLocationStatus
              (run after every PIL/Step1/override change) will already
              have moved the location to `done` — tapping just refetches
              + navigates back to the list.
            - We have no mutation for "complete the location" because all
              gating is server-side; the bottom CTA returns the mapper to
              the location list once everything is satisfied. */}
        {step1Done && (
        <section className="border-t px-4 py-4 space-y-2">
          {canMarkLocationDone ? (
            <>
              <p className="text-xs text-muted-foreground">
                Every required spot is mapped. Tap below to finish this location.
              </p>
              <Button
                type="button"
                onClick={() => {
                  utils?.share?.getMappingLocation?.invalidate?.({ token, locationId });
                  utils?.share?.listMappingLocations?.invalidate?.();
                  toast.success("Location marked done");
                  navigate({ to: "/mapping/$token", params: { token } } as any);
                }}
                className="w-full min-h-12 gap-2 bg-green-600 hover:bg-green-700 text-white"
                size="lg"
              >
                <Check className="h-5 w-5" strokeWidth={3} />
                Mark location done
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                On site and found another good potential spot that's not already listed? Tap below to add a new installation spot at your current location.
              </p>
              <Button
                type="button"
                onClick={handleAddAtCenter}
                disabled={createPIL.isPending || fetchingGps || pilsLocked}
                className="w-full min-h-12 gap-2"
              >
                {createPIL.isPending || fetchingGps ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
                {fetchingGps
                  ? "Getting GPS…"
                  : createPIL.isPending
                    ? "Adding spot…"
                    : "Add installation spot at my location"}
              </Button>
            </>
          )}
        </section>
        )}
      </div>
      <HelpFAB token={token} />
    </div>
  );
}

interface PILCardProps {
  pil: PILData;
  token: string;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  registerRef: (el: HTMLDivElement | null) => void;
  locked?: boolean;
  userPos?: { lat: number; lng: number; accuracy?: number } | null;
}

function PILCard({ pil, token, expanded, onToggle, onChanged, registerRef, locked, userPos }: PILCardProps) {
  const polePhotoCount = pil.photos.filter((p) => ["pole_wide", "pole_side", "pole_base"].includes(p.kind)).length;
  const isDone = pil.status === "done";
  const isRejected = pil.status === "rejected";
  const hasStartedInput =
    pil.is_viable !== null ||
    (pil.general_comments !== null && pil.general_comments.trim() !== "") ||
    pil.photos.length > 0;

  return (
    <div
      ref={registerRef}
      className={cn(
        "rounded-lg border transition-all",
        isDone && !isRejected
          ? "border-green-600 bg-green-50"
          : "bg-card",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 min-h-14"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className={cn(
              "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums",
              isRejected && "bg-gray-100 text-gray-600",
              !isRejected && isDone && "bg-green-600 text-white",
              !isRejected && !isDone && hasStartedInput && "bg-blue-100 text-blue-800",
              !isRejected && !isDone && !hasStartedInput && "bg-yellow-100 text-yellow-800",
            )}
            aria-label={`Installation spot ${pil.ordinal}, ${isRejected ? "rejected" : isDone ? "done" : hasStartedInput ? "in progress" : "not started"}`}
          >
            {isRejected ? (
              <X className="h-4 w-4" strokeWidth={3} />
            ) : isDone ? (
              <Check className="h-4 w-4" strokeWidth={3} />
            ) : (
              pil.ordinal
            )}
          </span>
          {pil.placed_by === "admin" && (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
              Pre-placed
            </span>
          )}
          {pil.placed_by === "mapper" && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Added by you
            </span>
          )}
          {isRejected && (
            <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700">
              Rejected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <ImageIcon className="h-3 w-3" />
            {polePhotoCount}/3
          </span>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t">
          <PILFormSheet
            token={token}
            pil={pil}
            onChanged={onChanged}
            locked={locked}
            userPos={userPos}
          />
        </div>
      )}
    </div>
  );
}
